import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Search,
  Wand2,
  X,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAllocationWorkspaceData } from "@/hooks/useAllocationData";
import type { Transaction } from "@/types/payments";
import type { AllocationItemRequest } from "@/types/api/allocations";

interface AllocationTransaction {
  id: number;
  transactionNumber: string;
  transactionType: string;
  customerId: number;
  customerName: string;
  transactionDate: Date;
  dueDate: Date;
  originalAmount: number;
  currentBalance: number;
  currency: string;
  daysOverdue: number;
  onDispute: boolean;
  isPromised?: boolean;
}

interface AllocationRow {
  transaction: AllocationTransaction;
  allocatedAmount: number;
  isSelected: boolean;
}

type FilterOption = "all" | "overdue" | "due_this_week" | "due_next_30_days";

function mapTransactionToAllocationTransaction(transaction: Transaction): AllocationTransaction {
  return {
    id: transaction.id,
    transactionNumber: transaction.transactionNumber,
    transactionType: transaction.transactionType,
    customerId: transaction.customerId,
    customerName: transaction.customerName,
    transactionDate: transaction.transactionDate,
    dueDate: transaction.dueDate,
    originalAmount: transaction.originalAmount,
    currentBalance: transaction.currentBalance,
    currency: transaction.currency,
    daysOverdue: transaction.daysOverdue,
    onDispute: transaction.onDispute,
    isPromised: transaction.isPromised,
  };
}

export default function AllocationWorkspace() {
  const { receiptId } = useParams<{ receiptId: string }>();
  const navigate = useNavigate();

  // Use mode-aware hook for data fetching (respects demo/live toggle)
  const {
    receipt,
    transactions,
    isLoading,
    error,
    createAllocations,
    isSubmitting,
  } = useAllocationWorkspaceData(receiptId ? Number(receiptId) : undefined);

  // Local state for allocation rows (derived from transactions)
  const [allocationRows, setAllocationRows] = useState<AllocationRow[]>([]);
  
  // UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOption, setFilterOption] = useState<FilterOption>("all");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Initialize allocation rows when transactions change
  useEffect(() => {
    if (transactions.length > 0) {
      setAllocationRows(
        transactions.map(t => ({
          transaction: mapTransactionToAllocationTransaction(t),
          allocatedAmount: 0,
          isSelected: false,
        }))
      );
    }
  }, [transactions]);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate totals
  const totalAllocated = useMemo(() => {
    return allocationRows.reduce((sum, row) => sum + row.allocatedAmount, 0);
  }, [allocationRows]);

  const remainingAmount = useMemo(() => {
    return (receipt?.receivedAmount || 0) - totalAllocated;
  }, [receipt, totalAllocated]);

  const allocationProgress = useMemo(() => {
    if (!receipt?.receivedAmount) return 0;
    return Math.min((totalAllocated / receipt.receivedAmount) * 100, 100);
  }, [totalAllocated, receipt]);

  const selectedCount = useMemo(() => {
    return allocationRows.filter(row => row.isSelected).length;
  }, [allocationRows]);

  const totalSelectedAmount = useMemo(() => {
    return allocationRows
      .filter(row => row.isSelected)
      .reduce((sum, row) => sum + row.transaction.currentBalance, 0);
  }, [allocationRows]);

  // Filter transactions
  const filteredRows = useMemo(() => {
    let filtered = allocationRows;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(row =>
        row.transaction.transactionNumber.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply status filter
    if (filterOption !== "all") {
      const now = new Date();
      filtered = filtered.filter(row => {
        const dueDate = new Date(row.transaction.dueDate);
        const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        switch (filterOption) {
          case "overdue":
            return row.transaction.daysOverdue > 0;
          case "due_this_week":
            return daysUntilDue >= 0 && daysUntilDue <= 7;
          case "due_next_30_days":
            return daysUntilDue >= 0 && daysUntilDue <= 30;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [allocationRows, searchQuery, filterOption]);

  // Get status badge for invoice
  const getStatusBadge = (transaction: AllocationTransaction) => {
    if (transaction.daysOverdue > 0) {
      return <Badge variant="destructive">Overdue</Badge>;
    }
    
    const now = new Date();
    const dueDate = new Date(transaction.dueDate);
    const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDue >= 0 && daysUntilDue <= 7) {
      return <Badge variant="default" className="bg-amber-500 hover:bg-amber-600">Due Soon</Badge>;
    }
    
    return null;
  };

  // Handle allocation amount change
  const handleAllocationChange = (index: number, value: string) => {
    const numValue = parseFloat(value) || 0;
    const row = filteredRows[index];
    const originalIndex = allocationRows.findIndex(r => r.transaction.id === row.transaction.id);
    
    // Validation: Cannot exceed invoice outstanding
    if (numValue > row.transaction.currentBalance) {
      toast.error("Cannot exceed invoice outstanding amount");
      return;
    }

    // Validation: Cannot exceed remaining payment amount
    const otherAllocations = allocationRows.reduce((sum, r, i) => {
      if (i === originalIndex) return sum;
      return sum + r.allocatedAmount;
    }, 0);

    if (otherAllocations + numValue > (receipt?.receivedAmount || 0)) {
      toast.error("Cannot exceed payment amount");
      return;
    }

    // Update allocation
    const newRows = [...allocationRows];
    newRows[originalIndex] = {
      ...newRows[originalIndex],
      allocatedAmount: numValue,
      isSelected: numValue > 0 ? true : newRows[originalIndex].isSelected,
    };
    setAllocationRows(newRows);
  };

  // Handle checkbox toggle
  const handleCheckboxToggle = (index: number, checked: boolean) => {
    const row = filteredRows[index];
    const originalIndex = allocationRows.findIndex(r => r.transaction.id === row.transaction.id);
    
    if (checked) {
      // When checking, set allocated amount to outstanding total (if not already set)
      const currentAllocated = allocationRows[originalIndex].allocatedAmount;
      const outstandingAmount = row.transaction.currentBalance;
      
      // Calculate how much we can allocate
      const otherAllocations = allocationRows.reduce((sum, r, i) => {
        if (i === originalIndex) return sum;
        return sum + r.allocatedAmount;
      }, 0);
      
      const availableAmount = (receipt?.receivedAmount || 0) - otherAllocations;
      const amountToAllocate = currentAllocated > 0 ? currentAllocated : Math.min(outstandingAmount, availableAmount);
      
      // Check if we have enough remaining payment amount
      if (amountToAllocate <= 0 || availableAmount <= 0) {
        toast.error("Insufficient payment amount remaining");
        return;
      }
      
      const newRows = [...allocationRows];
      newRows[originalIndex] = {
        ...newRows[originalIndex],
        isSelected: true,
        allocatedAmount: amountToAllocate,
      };
      setAllocationRows(newRows);
    } else {
      // When unchecking, clear the allocated amount
      const newRows = [...allocationRows];
      newRows[originalIndex] = {
        ...newRows[originalIndex],
        isSelected: false,
        allocatedAmount: 0,
      };
      setAllocationRows(newRows);
    }
  };

  // Auto-allocate promises
  const handleAutoAllocatePromises = () => {
    if (!receipt) return;

    let remaining = receipt.receivedAmount;
    const newRows = allocationRows.map(row => {
      if (row.transaction.isPromised && remaining > 0) {
        const toAllocate = Math.min(remaining, row.transaction.currentBalance);
        remaining -= toAllocate;
        return {
          ...row,
          allocatedAmount: toAllocate,
          isSelected: true,
        };
      }
      return {
        ...row,
        allocatedAmount: 0,
        isSelected: false,
      };
    });

    const promisedCount = newRows.filter(r => r.isSelected && r.transaction.isPromised).length;
    const totalAllocated = receipt.receivedAmount - remaining;
    
    setAllocationRows(newRows);
    
    if (promisedCount > 0) {
      toast.success("Promised invoices allocated", {
        description: `Allocated ${formatCurrency(totalAllocated)} to ${promisedCount} invoice${promisedCount > 1 ? 's' : ''} with Promise to Pay`,
      });
    } else {
      toast.info("No promised invoices found");
    }
  };

  // Auto-allocate (oldest first)
  const handleAutoAllocate = () => {
    if (!receipt) return;

    // Sort by due date (oldest first)
    const sortedRows = [...allocationRows].sort((a, b) => {
      return new Date(a.transaction.dueDate).getTime() - new Date(b.transaction.dueDate).getTime();
    });

    let remaining = receipt.receivedAmount;
    const newRows = sortedRows.map(row => {
      if (remaining <= 0) {
        return { ...row, allocatedAmount: 0, isSelected: false };
      }

      const toAllocate = Math.min(remaining, row.transaction.currentBalance);
      remaining -= toAllocate;

      return {
        ...row,
        allocatedAmount: toAllocate,
        isSelected: toAllocate > 0,
      };
    });

    setAllocationRows(newRows);
    toast.success("Auto-allocation complete", {
      description: `Allocated ${formatCurrency(receipt.receivedAmount - remaining)} to ${newRows.filter(r => r.isSelected).length} invoices`,
    });
  };

  // Clear all allocations
  const handleClearAllocation = () => {
    setAllocationRows(allocationRows.map(row => ({
      ...row,
      allocatedAmount: 0,
      isSelected: false,
    })));
    toast.success("Allocation cleared");
  };

  // Deselect all
  const handleDeselectAll = () => {
    setAllocationRows(allocationRows.map(row => ({
      ...row,
      allocatedAmount: 0,
      isSelected: false,
    })));
  };

  // Save as unapplied
  const handleSaveAsUnapplied = () => {
    toast.success("Allocation saved successfully", {
      description: "You can return to complete this allocation later",
    });
    navigate("/payments/receipts");
  };

  // Allocate and confirm
  const handleAllocateConfirm = () => {
    const allocatedInvoices = allocationRows.filter(row => row.allocatedAmount > 0);
    
    if (allocatedInvoices.length === 0) {
      toast.error("Please allocate at least one invoice");
      return;
    }

    setShowConfirmDialog(true);
  };

  // Confirm allocation - calls the API (respects demo/live mode)
  const confirmAllocation = async () => {
    if (!receipt) return;
    
    const allocatedInvoices = allocationRows.filter(row => row.allocatedAmount > 0);
    
    const allocationItems: AllocationItemRequest[] = allocatedInvoices.map(row => ({
      transactionId: row.transaction.id,
      allocatedAmount: row.allocatedAmount,
      allocationMethod: "Manual",
    }));
    
    try {
      await createAllocations({
        receiptId: receipt.id,
        allocations: allocationItems,
      });
      
      toast.success("Payment allocated successfully", {
        description: `Allocated ${formatCurrency(totalAllocated)} to ${allocatedInvoices.length} invoice${allocatedInvoices.length > 1 ? "s" : ""}`,
      });
      
      setShowConfirmDialog(false);
      navigate("/payments/receipts");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create allocations";
      toast.error("Failed to allocate payment", { description: errorMessage });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading allocation data...</p>
        </div>
      </div>
    );
  }

  if (error || !receipt) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold">{error?.message || "Receipt not found"}</h1>
          <Button onClick={() => navigate("/payments/receipts")}>
            Back to Receipts
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-4 p-4 md:p-6 pt-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/payments/receipts")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Receipts
        </Button>
      </div>

      <div>
        <h2 className="text-3xl font-bold tracking-tight">Payment Allocation</h2>
        <p className="text-muted-foreground">
          Allocate payment to invoices for <span className="font-bold">{receipt.customerName}</span> ({receipt.customerAccountNumber})
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Main Content - Left Panel (2/3 width) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Invoice Selection Table */}
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by invoice number..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>
                <Select value={filterOption} onValueChange={(value) => setFilterOption(value as FilterOption)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Invoices</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="due_this_week">Due This Week</SelectItem>
                    <SelectItem value="due_next_30_days">Due Next 30 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Bulk Selection Row */}
              {selectedCount > 0 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedCount === filteredRows.length}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          // Select all visible
                          const newRows = [...allocationRows];
                          filteredRows.forEach(row => {
                            const index = newRows.findIndex(r => r.transaction.id === row.transaction.id);
                            if (index !== -1) {
                              newRows[index] = { ...newRows[index], isSelected: true };
                            }
                          });
                          setAllocationRows(newRows);
                        } else {
                          handleDeselectAll();
                        }
                      }}
                    />
                    <span className="text-sm font-medium">
                      {selectedCount} invoice{selectedCount > 1 ? 's' : ''} selected
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleDeselectAll}
                      className="h-auto p-0 text-sm"
                    >
                      Deselect All
                    </Button>
                  </div>
                  <div className="text-sm font-medium">
                    Total Selected: {formatCurrency(totalSelectedAmount)}
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Reference Number</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Original Amount</TableHead>
                      <TableHead className="text-right">Outstanding</TableHead>
                      <TableHead className="text-right w-[180px]">Allocated Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No invoices match your filters
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRows.map((row, index) => (
                        <TableRow key={row.transaction.id}>
                          <TableCell>
                            <Checkbox
                              checked={row.isSelected}
                              onCheckedChange={(checked) => handleCheckboxToggle(index, checked as boolean)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{row.transaction.transactionNumber}</span>
                              {row.transaction.isPromised && (
                                <Badge variant="outline" className="text-xs border-blue-500 bg-blue-50 text-blue-700">
                                  P2P
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(row.transaction)}
                          </TableCell>
                          <TableCell>
                            {format(new Date(row.transaction.transactionDate), 'dd/MM/yyyy')}
                          </TableCell>
                          <TableCell>
                            <span className={cn(
                              row.transaction.daysOverdue > 0 && "text-red-600 font-medium"
                            )}>
                              {format(new Date(row.transaction.dueDate), 'dd/MM/yyyy')}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(row.transaction.originalAmount)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.transaction.currentBalance)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                £
                              </span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max={row.transaction.currentBalance}
                                value={row.allocatedAmount > 0 ? row.allocatedAmount.toFixed(2) : ''}
                                onChange={(e) => handleAllocationChange(index, e.target.value)}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  handleAllocationChange(index, value.toFixed(2));
                                }}
                                className="pl-6 text-right"
                                placeholder="0.00"
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel (1/3 width) */}
        <div className="space-y-4">
          {/* Payment Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reference:</span>
                <span className="font-medium">{receipt.paymentReference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Payment Date:</span>
                <span className="font-medium">
                  {format(new Date(receipt.receiptDate), 'dd/MM/yyyy')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Original Amount:</span>
                <span className="font-medium">{formatCurrency(receipt.receivedAmount)}</span>
              </div>
              <div className="pt-2 border-t">
                <Badge variant="default" className="bg-amber-500 hover:bg-amber-600">
                  Pending Allocation
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Allocation Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle>Allocation Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-muted-foreground">Total Allocated:</span>
                  <span className="text-2xl font-bold text-green-600">
                    {formatCurrency(totalAllocated)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-muted-foreground">Remaining Unallocated:</span>
                  <span className="text-2xl font-bold text-orange-600">
                    {formatCurrency(remainingAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Invoices Selected:</span>
                  <span className="font-medium">{selectedCount}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Progress value={allocationProgress} accent="teal" />
                <div className="text-xs text-muted-foreground text-center">
                  {allocationProgress.toFixed(0)}% allocated
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleAutoAllocatePromises}
            >
              <Wand2 className="mr-2 h-4 w-4" />
              Auto-Allocate Promises
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleAutoAllocate}
            >
              <Wand2 className="mr-2 h-4 w-4" />
              Auto-Allocate (Oldest First)
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleClearAllocation}
            >
              <X className="mr-2 h-4 w-4" />
              Clear Allocation
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleSaveAsUnapplied}
            >
              Save as Unallocated
            </Button>
            <Button
              className="w-full"
              onClick={handleAllocateConfirm}
            >
              Allocate & Confirm
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Allocation</DialogTitle>
            <DialogDescription>
              Are you sure you want to allocate {formatCurrency(totalAllocated)} to{' '}
              {allocationRows.filter(r => r.allocatedAmount > 0).length} invoice
              {allocationRows.filter(r => r.allocatedAmount > 0).length > 1 ? 's' : ''}?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Payment Amount:</span>
              <span className="font-medium">{formatCurrency(receipt.receivedAmount)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total Allocated:</span>
              <span className="font-medium text-green-600">{formatCurrency(totalAllocated)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Remaining:</span>
              <span className="font-medium text-orange-600">{formatCurrency(remainingAmount)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={confirmAllocation} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Allocating...
                </>
              ) : (
                "Confirm Allocation"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
